import { networkApi } from '../infrastructure/api/NetworkApiClient';

// §14a interventions (plans/steuerung-cls.md 3.8, backend ControlController). Instance-wide endpoints, never with a
// zone prefix. Powers in W. Timestamps are UTC instants — parse them with parseUtcTimestampMs.

export type ControlActionState =
  | 'proposed' | 'approved' | 'sending' | 'active' | 'releasing' | 'ended'
  | 'expired' | 'rejected' | 'failed' | 'cancelled';

export const OPEN_ACTION_STATES: ControlActionState[] = ['proposed', 'approved', 'sending', 'active', 'releasing'];
export const RUNNING_ACTION_STATES: ControlActionState[] = ['approved', 'sending', 'active', 'releasing'];

/** A SteuVE behind a target at planning time (backend ControlTargetDevice, migration 0075). */
export interface ControlTargetDevice {
  id: string;
  trId?: string;
  /** resource type: wallbox | heat_pump | air_conditioning | battery_storage | other */
  type?: string;
  name?: string;
  ratedKw: number;
}

export interface ControlActionTarget {
  id: string;
  houseConnectionId: string;
  zoneId: string;
  steuerboxId?: string;
  channelId?: string;
  boxSerial?: string;
  clsIndex?: number;
  srId?: string;
  neloId?: string;
  /** direct (per SteuVE) | ems (one setpoint per Netzanschluss, Ziff. 4.4) */
  controlPath?: 'direct' | 'ems';
  /** SR-/NeLo-ID the command addresses (BDEW path) */
  locationId?: string;
  /** the SteuVE this channel dims */
  devices?: ControlTargetDevice[];
  msbName?: string;
  makoKeyPresent: boolean;
  boxOnline?: boolean;
  groupIndex: number;
  startOffsetS: number;
  pSteuVeW: number;
  pMinW: number;
  pIstW: number;
  /** smgw | powerflow | gzf_estimate */
  pIstSource: string;
  pRedMaxW: number;
  pRedIstW: number;
  /** Expected reduction at the effective limit (STEUERUNG.md 7.2 Nr. 15); 0 when skipped. */
  expectedReductionW?: number;
  requestedLimitW: number;
  effectiveLimitW: number;
  /** relay_binary | nearest_level | exact */
  effectiveReason: string;
  /** planned | skipped | sent | confirmed | unreachable | failed | released */
  state: string;
  skipReason?: string;
  address?: string;
}

export interface ControlCommand {
  id: string;
  targetId?: string;
  kind: string;
  scheduleId?: string;
  clsIndex?: number;
  boxSerial?: string;
  valueW?: number;
  valueSentKw?: number;
  startAt?: string;
  endAt?: string;
  attempt: number;
  sentAt: string;
  answeredAt?: string;
  httpStatus?: number;
  success: boolean;
  errorKind?: string;
  errorText?: string;
  latencyMs?: number;
  dispatcher: string;
  /** BDEW path: addressed SR-/NeLo-ID and the MSB's asynchronous answers */
  transactionId?: string;
  locationId?: string;
  preliminaryState?: 'possible' | 'rejected';
  finalState?: 'succeeded' | 'failed' | 'unknown';
  finalReason?: string;
  finalAt?: string;
}

export interface ControlSupplierInfo {
  actionId: string;
  startAt?: string;
  endAt?: string;
  withoutMarketLocation: number;
  rows: {
    marketLocationId?: string;
    meteringLocationId?: string;
    houseConnectionId: string;
    address?: string;
    controlPath?: string;
    locationId?: string;
    netzbereich: string;
    start?: string;
    end?: string;
  }[];
}

export interface VnbDigitalReport {
  month: string;
  label: string;
  dueDate: string;
  rows: {
    areaId: string;
    name: string;
    nebeId?: string;
    postalCode?: string;
    kind: 'curative' | 'preventive';
    kindLabel: string;
    steuVeCount: number;
    durationHours: number;
    intensityPct: number;
    measure?: string;
    measureLabel?: string;
    completionLabel?: string;
    findings: string[];
  }[];
}

export interface ControlAreaStatus {
  areaId: string;
  nebeId?: string;
  name: string;
  postalCode?: string;
  controlled: boolean;
  kind?: 'curative' | 'preventive';
  since?: string;
  expectedUntil?: string;
  steuVe: number;
  nzeStatus?: string;
  /** Zones of the Netzbereich (dashboard card per zone). */
  zoneIds?: string[];
  /** Share of connections with minute values and the share the FNN basis requires (STEUERUNG.md 7.2 Nr. 17). */
  nzeSharePct?: number;
  nzeRequiredPct?: number;
  nzeText?: string;
}

/** Label of a command channel (CONTROL_DISPATCHER). */
export const DISPATCHER_LABEL: Record<string, string> = {
  simulation: 'Simulation',
  maas: 'maas (Feld)',
  bdew: 'BDEW-API an den Messstellenbetreiber',
};

export const dispatcherLabel = (d?: string) => (d ? DISPATCHER_LABEL[d] ?? d : 'Simulation');

/** Text of the MSB's answer to a BDEW command (null on the other paths). */
export function msbAnswerText(c: ControlCommand): string | null {
  if (c.finalState === 'succeeded') return 'MSB: ausgeführt';
  if (c.finalState === 'failed') {
    return c.finalReason === 'communicationFailure' ? 'MSB: Steuerbox nicht erreichbar'
      : c.finalReason === 'overload' ? 'MSB: überlastet'
        : c.finalReason === 'unable' ? 'MSB: nicht möglich' : 'MSB: fehlgeschlagen';
  }
  if (c.finalState === 'unknown') return 'MSB: Ergebnis offen';
  if (c.preliminaryState === 'possible') return 'MSB: angenommen';
  return c.dispatcher === 'bdew' && c.success ? 'MSB: Antwort ausstehend' : null;
}

/** A Netzbereich of an intervention (backend control_action_areas, STEUERUNG.md 8.3). */
export interface ControlActionArea {
  areaId: string;
  name?: string;
  nebeId?: string;
  zoneIds: string[];
  role: 'primary' | 'bundled';
  sharePct?: number;
  reason?: string;
  preselected: boolean;
  included: boolean;
}

export interface ControlAction {
  id: string;
  /** Load-flow context (Netzverbund); the control unit is the Netzbereich (areaId). */
  verbundId: string;
  areaId?: string;
  areaIds: string[];
  areas?: ControlActionArea[];
  zoneIds: string[];
  triggerZoneId: string;
  state: ControlActionState;
  triggerKind: 'trafo_overload' | 'cable_overload' | 'undervoltage' | 'preventive';
  /** curative (network-oriented) | preventive (Ziff. 10.5) */
  kind?: 'curative' | 'preventive';
  /** approval mode of the proposal (proposal | auto | auto_after_timeout | auto_outside_hours) */
  mode?: string;
  /** factor that went into P_lim (reductionFactor = FNN factor, share of the potential) */
  commandFactor?: number;
  /** NZE basis of the detection the intervention was proposed on */
  nzeSharePct?: number;
  nzeRequiredPct?: number;
  triggerElementId: string;
  triggerElementType: string;
  triggerSource: string;
  nzeAt: string;
  measuredValue: number;
  limitValue: number;
  unit: '%' | 'V' | '';
  operationalFactor?: number;
  flowW?: number;
  requiredReductionW?: number;
  safetyMarginPct: number;
  unreachableSharePct: number;
  potentialW: number;
  reductionFactor: number;
  insufficientPotential: boolean;
  steuVeCount: number;
  netzanschlussCount: number;
  plannedStartAt: string;
  plannedEndAt: string;
  actualStartAt?: string;
  actualEndAt?: string;
  proposedAt: string;
  expiresAt: string;
  approvedAt?: string;
  approvedBy?: string;
  decidedAt?: string;
  decidedBy?: string;
  decisionNote?: string;
  dispatcher: string;
  reasonText: string;
  /** Extension chain (phase 8): the slot this one follows. */
  parentActionId?: string;
  /** continue = trigger still present; hold = Nachholeffekt after it cleared. */
  extensionKind?: ControlExtensionKind;
  scheduleId?: string;
  targets?: ControlActionTarget[];
  commands?: ControlCommand[];
}

export interface ControlVerbundActivity {
  /** Netzbereich id (historic field name). */
  verbundId: string;
  areaName?: string;
  nebeId?: string;
  zoneIds: string[];
  interventions: number;
  lastAt: string;
}

export interface ControlOverview {
  openProposals: number;
  activeInterventions: number;
  interventionsLast90Days: number;
  recentVerbuende: ControlVerbundActivity[];
}

export interface ControlPowerLogRow {
  houseConnectionId: string;
  loggedAt: string;
  pTotalW?: number;
  pL1W?: number;
  pL2W?: number;
  pL3W?: number;
  /** taf10 | powerflow | none */
  source: string;
  /** measured | estimated | missing */
  quality: string;
  isBaseline: boolean;
}

export interface ControlEvidenceSlot {
  actionId: string;
  state: ControlActionState;
  extensionKind?: ControlExtensionKind;
  scheduleId?: string;
  plannedStartAt: string;
  plannedEndAt: string;
  approvedAt?: string;
  approvedBy?: string;
  decisionNote?: string;
  reasonText: string;
}

export interface ControlEvidenceHouse {
  houseConnectionId: string;
  zoneId: string;
  address?: string;
  boxSerial?: string;
  clsIndex?: number;
  srId?: string;
  neloId?: string;
  controlPath?: string;
  locationId?: string;
  devices?: ControlTargetDevice[];
  msbName?: string;
  requestedLimitW: number;
  effectiveLimitW: number;
  effectiveReason: string;
  state: string;
  baselineW?: number;
  minDuringW?: number;
  avgDuringW?: number;
  avgAfterW?: number;
  loggedMinutes: number;
  measuredMinutes: number;
  powerLog: ControlPowerLogRow[];
}

/** Evidence of one intervention chain (BK6-22-300 Ziff. 7.1 a–c). */
export interface ControlEvidence {
  rootActionId: string;
  verbundId: string;
  zoneIds: string[];
  /** Netzbereiche (NeBe) of the intervention, primary first. */
  areas: ControlActionArea[];
  steuVeCount: number;
  netzanschlussCount: number;
  triggerKind: ControlAction['triggerKind'];
  triggerElementId: string;
  triggerSource: string;
  measuredValue: number;
  limitValue: number;
  unit: '%' | 'V';
  nzeAt: string;
  nzeSnapshotZoneId?: string;
  proposedAt: string;
  approvedAt?: string;
  approvedBy?: string;
  firstCommandSentAt?: string;
  firstCommandAnsweredAt?: string;
  startAt?: string;
  endAt?: string;
  durationMinutes: number;
  finalState: ControlActionState;
  dispatcher: string;
  slots: ControlEvidenceSlot[];
  houses: ControlEvidenceHouse[];
  commands: ControlCommand[];
}

/** Unit check of one manufacturer / FNN version (backend control_unit_verifications). */
export interface ControlUnitVerification {
  manufacturer: string;
  fnnVersion: string;
  verified: boolean;
  unit?: string;
  sentKw?: number;
  readValue?: number;
  verifiedAt: string;
  verifiedBy: string;
  note?: string;
}

/** One item of the pilot checklist (backend ControlReadinessEvaluator). */
export interface ControlReadinessItem {
  key: string;
  label: string;
  status: 'ok' | 'warn' | 'fail';
  detail: string;
}

export interface ControlReadiness {
  dispatcher: string;
  items: ControlReadinessItem[];
}

/** § 14a demo on the test instance (backend ControlDemoController → FakeRegistry). */
export interface ControlDemoScenario {
  zoneId: string;
  activeKw: number;
  activeDevices: number;
  targetPercent: number;
  capacityKva: number;
  startedUtc: string;
  untilUtc: string;
}

export interface ControlDemoZone {
  zoneId: string;
  zoneName: string;
  boxes: number;
  controllableKw: number;
  baseKva: number;
  demandKw: number;
  drawKw: number;
  limitedBoxes: number;
  capacityKva: number;
  measuredStations: number;
  scenario?: ControlDemoScenario | null;
}

/** A measured Gefährdung (control_trigger_events); noProposalKind = why it did not become a proposal. */
export interface ControlTriggerEvent {
  id: string;
  zoneId: string;
  areaId?: string | null;
  triggerKind: ControlAction['triggerKind'];
  elementId: string;
  measuredValue: number;
  limitValue: number;
  unit: string;
  nzeAt: string;
  detectedAt: string;
  coalescedInto?: string | null;
  actionId?: string | null;
  noProposalKind?: 'nze_insufficient' | 'no_area' | 'no_steuve' | 'no_potential' | 'unreachable' | 'error' | null;
  noProposalReason?: string | null;
}

export const NO_PROPOSAL_KIND_LABEL: Record<NonNullable<ControlTriggerEvent['noProposalKind']>, string> = {
  nze_insufficient: 'Netzzustandsermittlung nicht ausreichend',
  no_area: 'Kein Netzbereich',
  no_steuve: 'Keine steuerbare SteuVE',
  no_potential: 'Kein Reduktionspotenzial',
  unreachable: 'Keine SteuVE ansteuerbar',
  error: 'Fehler bei der Berechnung',
};

export const controlService = {
  /** Newest measured Gefährdungen of all Netzbereiche (incl. the ones without a proposal and why). */
  getTriggerEvents(limit = 200): Promise<ControlTriggerEvent[]> {
    return networkApi.httpGet<ControlTriggerEvent[]>(`/control/triggers?limit=${limit}`);
  },

  /** Gefährdungen that did not become a proposal (with the reason) of the last hours - filtered on the server. */
  getWithoutProposal(hours = 24): Promise<ControlTriggerEvent[]> {
    return networkApi.httpGet<ControlTriggerEvent[]>(`/control/triggers/without-proposal?hours=${hours}`);
  },

  getDemo(): Promise<{ zones: ControlDemoZone[] }> {
    return networkApi.httpGet<{ zones: ControlDemoZone[] }>('/control/demo');
  },

  startDemo(zoneId: string, targetPercent: number, minutes: number): Promise<{ reachablePercent: number }> {
    return networkApi.httpPost<{ reachablePercent: number }>('/control/demo/overload', { zoneId, targetPercent, minutes });
  },

  /** Registers the FakeRegistry's two MSB and confirms their SR-IDs (BDEW path of the demo). */
  setupDemoBdew(): Promise<{ confirmed: number }> {
    return networkApi.httpPost<{ confirmed: number }>('/control/demo/bdew-setup', {});
  },

  stopDemo(zoneId?: string): Promise<unknown> {
    return networkApi.httpPost<unknown>('/control/demo/stop', { zoneId: zoneId ?? null });
  },

  /** Pilot readiness of the instance: master data, boxes, measurement, settings, observation, command channel. */
  getReadiness(): Promise<ControlReadiness> {
    return networkApi.httpGet<ControlReadiness>('/control/readiness');
  },

  getUnitVerifications(): Promise<ControlUnitVerification[]> {
    return networkApi.httpGet<ControlUnitVerification[]>('/control/unit-verifications');
  },

  /** Admin confirmation of a blocked / unchecked combination. */
  confirmUnit(manufacturer: string, fnnVersion: string, note?: string): Promise<ControlUnitVerification[]> {
    return networkApi.httpPost<ControlUnitVerification[]>(
      `/control/unit-verifications/${encodeURIComponent(manufacturer)}/${encodeURIComponent(fnnVersion)}/confirm`, { note: note || null });
  },

  /** Evidence record of the chain an intervention belongs to, with the 1-min power log per house. */
  /** Ziff. 8.3 c: Marktlokationen of an intervention with start/end for the supplier information. */
  getSupplierInfo(id: string): Promise<ControlSupplierInfo> {
    return networkApi.httpGet<ControlSupplierInfo>(`/control/actions/${id}/supplier-info`);
  },

  /** Ziff. 7.4: ZIP with every intervention of the period (optionally one Netzbereich) for the BNetzA. */
  exportEvidence(fromUtc: string, toUtc: string, areaId?: string): Promise<Blob> {
    const q = new URLSearchParams({ fromUtc, toUtc });
    if (areaId) q.set('areaId', areaId);
    return networkApi.httpGetBlob(`/control/export?${q.toString()}`);
  },

  /** Ziff. 8.4: monthly VNBdigital list per Netzbereich ("YYYY-MM"). */
  getVnbDigital(month: string): Promise<VnbDigitalReport> {
    return networkApi.httpGet<VnbDigitalReport>(`/control/vnbdigital?month=${encodeURIComponent(month)}`);
  },

  /** Ziff. 8.2 a: status of network-oriented control per Netzbereich. */
  getAreaStatus(): Promise<ControlAreaStatus[]> {
    return networkApi.httpGet<ControlAreaStatus[]>('/control/status');
  },

  getEvidence(id: string): Promise<ControlEvidence> {
    return networkApi.httpGet<ControlEvidence>(`/control/actions/${id}/evidence`);
  },

  /** Open interventions plus the closed ones of the last `days` days (journal). */
  getActions(days = 30): Promise<ControlAction[]> {
    return networkApi.httpGet<ControlAction[]>(`/control/actions?days=${days}`);
  },

  /** Interventions of the Netzbereich of a zone (open + last day) for the zone banner. */
  getForZone(zoneId: string): Promise<ControlAction[]> {
    return networkApi.httpGet<ControlAction[]>(`/control/actions/zone/${encodeURIComponent(zoneId)}`);
  },

  getAction(id: string): Promise<ControlAction> {
    return networkApi.httpGet<ControlAction>(`/control/actions/${id}`);
  },

  /** includeAreaIds = bundled Netzbereiche the operator includes (undefined = as proposed). */
  approve(id: string, note?: string, includeAreaIds?: string[]): Promise<ControlAction> {
    return networkApi.httpPost<ControlAction>(`/control/actions/${id}/approve`, { note: note || null, includeAreaIds: includeAreaIds ?? null });
  },

  reject(id: string, note?: string): Promise<ControlAction> {
    return networkApi.httpPost<ControlAction>(`/control/actions/${id}/reject`, { note: note || null });
  },

  /** Not-Aus: disables every sent schedule of the intervention. */
  cancel(id: string, note?: string): Promise<ControlAction> {
    return networkApi.httpPost<ControlAction>(`/control/actions/${id}/cancel`, { note: note || null });
  },

  getOverview(): Promise<ControlOverview> {
    return networkApi.httpGet<ControlOverview>('/control/overview');
  },
};

/** Kind of an extension slot (STEUERUNG.md 7.2 Nr. 12): the trigger persists, hold for the Nachholeffekt, or one release step. */
export type ControlExtensionKind = 'continue' | 'hold' | 'step';

/** Label and explanation of an extension slot. */
export const EXTENSION_KIND_LABEL: Record<ControlExtensionKind, { label: string; title: string }> = {
  continue: { label: 'Verlängerung', title: 'Die Gefährdung besteht fort – geschätzte Last ohne Steuerung über der Leistungsgrenze oder weiter gemeldet' },
  hold: { label: 'Halten', title: 'Entlastung gemessen, die Reduzierung bleibt eine Scheibe wegen des Nachholeffekts' },
  step: { label: 'Rücknahmestufe', title: 'Schrittweise Rücknahme (Ziff. 4.3 Satz 3): geringere Reduzierung, Relais per Los freigegeben' },
};

/** German labels (UI). */
export const ACTION_STATE_LABEL: Record<ControlActionState, string> = {
  proposed: 'Vorschlag',
  approved: 'Freigegeben',
  sending: 'Wird gesendet',
  active: 'Aktiv',
  releasing: 'Rücknahme',
  ended: 'Beendet',
  expired: 'Abgelaufen',
  rejected: 'Abgelehnt',
  failed: 'Fehlgeschlagen',
  cancelled: 'Abgebrochen (Not-Aus)',
};

export const TRIGGER_KIND_LABEL: Record<ControlAction['triggerKind'], string> = {
  trafo_overload: 'Trafo-Überlastung',
  cable_overload: 'Kabel-Überlastung',
  undervoltage: 'Unterspannung',
  preventive: 'Präventive Steuerung',
};

export const TARGET_STATE_LABEL: Record<string, string> = {
  planned: 'geplant',
  skipped: 'ausgelassen',
  sent: 'gesendet',
  confirmed: 'bestätigt',
  unreachable: 'nicht erreichbar',
  failed: 'Fehler',
  released: 'zurückgenommen',
};

export const P_IST_SOURCE_LABEL: Record<string, string> = {
  smgw: 'SMGW',
  powerflow: 'Lastfluss',
  gzf_estimate: 'erwartet (betrieblicher GZF)',
};

export const EFFECTIVE_REASON_LABEL: Record<string, string> = {
  relay_binary: 'Relais: Mindestleistung der Anlage',
  nearest_level: 'nächstgeringere Stufe',
  exact: 'exakt',
  running_slot: 'Wert der laufenden Scheibe gehalten',
};
